import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslator } from "../../context/translator";
import { getContractsByCompanyOrderedService } from "../../services/contract/services";
import Loading from "../loading/Loading";
import Pagination from "../pagination/Pagination";
import NoResults from "../noResults/noResults";
import { useNavigate } from "react-router-dom";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const Home2 = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { translate } = useTranslator();
  const [contractData, setContractData] = useState([]);
  // track page number
  const [page, setPage] = useState(1);
  // total number of records
  const [pageNumber, setPageNumber] = useState(0);
  // track API response time
  const [isLoading, setIsLoading] = useState(false);
  const [totalContract, setTotalContract] = useState(0);
  //add styles for each type of date
  const stylesDate = {
    [-1]: "text-red-500 font-semibold", // Expirado
    1: "text-orange-500 font-medium", // Expira en < 1 mes
    2: "text-yellow-500 font-medium", // Expira en 1-2 meses
    3: "text-green-600 font-medium", // Expira en > 2 meses
    0: "text-gray-400 italic", // Fecha inválida
  };

  const getContracts = React.useCallback(async () => {
    setIsLoading(true);

    const data = await getContractsByCompanyOrderedService({
      companyId: company?._id || "",
      page,
      pageSize: "10",
    });

    setIsLoading(false);
    setContractData(data?.contracts ?? []);
    const count = data?.totalContracts ?? 0;
    setTotalContract(count);
    setPageNumber(Math.ceil(count / 5));
  }, [company?._id, page]);

  useEffect(() => {
    getContracts();
  }, [getContracts]);

  const viewContract = (contractId, customerId) => {
    return navigate(`/clients/${customerId}/${contractId}`);
  };
  const viewCustomer = (customerId) => {
    return navigate(`/clients/${customerId}`);
  };

  function classifyTargetDate(targetDate) {
    const now = new Date();
    const target = new Date(targetDate);

    if (isNaN(target)) {
      return 0; // invalid date
    }

    const diffMs = target - now;

    if (diffMs < 0) {
      return -1; // date is in the past
    }

    const oneMonthMs = 30.44 * 24 * 60 * 60 * 1000; // approx one month
    const twoMonthsMs = 2 * oneMonthMs;

    if (diffMs < oneMonthMs) {
      return 1;
    } else if (diffMs < twoMonthsMs) {
      return 2;
    } else {
      return 3;
    }
  }

  if (userLoading || companyLoading) return null;

  return (
    <div className="container lg:py-3 max-w-screen-lg ">
      <h1 className="text-2xl font-semibold text-gray-800 mb-4">
        Welcome {userProfile?.user?.firstName} {userProfile?.user?.lastName}
      </h1>

      <div className="grid w-full gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
        <div className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-4">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-700 mb-3 dark:text-white">
              {translate("contract-expire-soon")}
            </h2>
            <p className="text-sm text-gray-600 dark:text-white">
              {translate("legend")}
            </p>
            <ul className="text-sm flex gap-4 flex-wrap mt-2">
              <li className="text-red-500">● {translate("expired")}</li>
              <li className="text-orange-500">
                ● {translate("expiring-soon")}
              </li>
              <li className="text-yellow-500">● {translate("expiring-mid")}</li>
              <li className="text-green-600">● {translate("valid-long")}</li>
              <li className="text-gray-400">● {translate("invalid-date")}</li>
            </ul>
          </div>
          {!isLoading && contractData.length === 0 && (
            <NoResults message={translate("no-contracts-found")} />
          )}
          {contractData.length === 0 && isLoading && <Loading />}
          {contractData.length > 0 && (
            <div>
              {/*add legend about what meant which color of the date */}
              <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-6 py-3">
                      {translate("contract-name")}
                    </th>
                    <th scope="col" className="px-6 py-3">
                      {translate("shop")}
                    </th>
                    <th scope="col" className="px-6 py-3">
                      {translate("date")}
                    </th>
                    <th scope="col" className="px-6 py-3 text-right">
                      <span>{translate("actions")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contractData.map((contract) => {
                    return (
                      <tr
                        key={contract._id}
                        className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        <th
                          scope="row"
                          className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                        >
                          {contract.contractName}
                        </th>
                        <th
                          scope="row"
                          className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                        >
                          {contract.customer?.shopName}
                        </th>
                        <td
                          className={
                            stylesDate[
                              classifyTargetDate(contract.dateExpiration)
                            ]
                          }
                        >
                          <div>
                            {new Date(
                              contract.dateExpiration,
                            ).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-4">
                            <button
                              type="button"
                              className="font-medium text-blue-600 dark:text-blue-500 hover:underline"
                              onClick={() => {
                                viewContract(contract._id, contract.customerId);
                              }}
                            >
                              {translate("view-contract")}
                            </button>
                            <button
                              type="button"
                              className="font-medium text-blue-600 dark:text-blue-500 hover:underline"
                              onClick={() => {
                                viewCustomer(contract.customerId);
                              }}
                            >
                              {translate("view-customer")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {isLoading && <Loading />}
              {!isLoading && (
                <Pagination
                  key={"paginationContract"}
                  data={contractData}
                  page={page}
                  pageNumber={pageNumber}
                  isLoading={isLoading}
                  setPage={setPage}
                  totalRecords={totalContract}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home2;
